import { useEffect, useState } from 'react';
import { Polyline } from '@react-google-maps/api';
import { useGoogleApiFlag, trackGoogleApiUsage } from '../../hooks/useGoogleApiFlags';

/**
 * Decodes a Google encoded polyline string into an array of {lat, lng}.
 * Reference: https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 */
function decodePolyline(encoded) {
  if (!encoded) return [];
  const points = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

const ROUTES_ENDPOINT = 'https://routes.googleapis.com/directions/v2:computeRoutes';
const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

const cache = new Map(); // key -> { path, distanceMeters, durationSeconds }

async function fetchRoute(origin, destination) {
  const key = `${origin.lat.toFixed(5)},${origin.lng.toFixed(5)}->${destination.lat.toFixed(5)},${destination.lng.toFixed(5)}`;
  if (cache.has(key)) return { ...cache.get(key), fromCache: true };
  const body = {
    origin: { location: { latLng: { latitude: origin.lat, longitude: origin.lng } } },
    destination: { location: { latLng: { latitude: destination.lat, longitude: destination.lng } } },
    travelMode: 'DRIVE',
    polylineQuality: 'HIGH_QUALITY',
  };
  const res = await fetch(ROUTES_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': API_KEY,
      'X-Goog-FieldMask': 'routes.polyline.encodedPolyline,routes.distanceMeters,routes.duration',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Routes API ${res.status}`);
  const json = await res.json();
  const r = json?.routes?.[0];
  if (!r?.polyline?.encodedPolyline) throw new Error('No route');
  const out = {
    path: decodePolyline(r.polyline.encodedPolyline),
    distanceMeters: r.distanceMeters,
    durationSeconds: r.duration ? parseInt(r.duration, 10) : null,
  };
  cache.set(key, out);
  return out;
}

/**
 * <RoutePolyline> — drop-in replacement for <DirectionsRenderer>.
 * Uses the new Routes API (computeRoutes) instead of the deprecated DirectionsService.
 *
 * Props:
 *   origin       {lat, lng}  — required
 *   destination  {lat, lng}  — required
 *   options      polyline render opts (strokeColor, strokeWeight, strokeOpacity)
 *   onRoute      callback({distanceMeters, durationSeconds, path}) — fires when route resolves
 *   usageKey     One of:
 *                 polyline_booking_live_tracker
 *                 polyline_admin_live_tracking
 *                 polyline_trip_monitoring_map
 *                 polyline_live_trip_modal
 *                Decides which Google API flag to honour and which usage
 *                counter to bump.  Defaults to admin live tracking so
 *                callers without a key remain conservative.
 */
export default function RoutePolyline({ origin, destination, options, onRoute, usageKey = 'polyline_admin_live_tracking' }) {
  const [path, setPath] = useState(null);
  // Phase 78az / 78ba — admin can disable road-routed polylines (Routes API)
  // per usage site from the "Google API" settings page.  When disabled, we
  // render a simple straight geodesic line so the map still shows
  // direction-of-travel, but no billable Routes API call is made.
  const polylinesOn = useGoogleApiFlag(usageKey);

  useEffect(() => {
    let cancelled = false;
    if (!polylinesOn || !origin?.lat || !destination?.lat) {
      setPath(null);
      return undefined;
    }
    fetchRoute(origin, destination)
      .then(r => {
        if (cancelled) return;
        setPath(r.path);
        // Phase 78ba — bump the usage counter only when a fresh fetch
        // hit the network.  In-process cache hits don't bill.
        if (!r.fromCache) trackGoogleApiUsage(usageKey, 1);
        if (onRoute) onRoute(r);
      })
      .catch(() => { if (!cancelled) setPath(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [polylinesOn, origin?.lat, origin?.lng, destination?.lat, destination?.lng, usageKey]);

  // Fallback: straight geodesic line when flag is off OR Routes API call
  // failed.  Free and still useful — booker / dispatcher can see the
  // direction the truck is heading.
  if (!path) {
    if (!origin?.lat || !destination?.lat) return null;
    return (
      <Polyline
        path={[{ lat: origin.lat, lng: origin.lng }, { lat: destination.lat, lng: destination.lng }]}
        options={{
          strokeColor: '#002FA7',
          strokeWeight: 3,
          strokeOpacity: 0.55,
          geodesic: true,
          ...(options || {}),
          // Always honour caller-provided strokeColor but lower the weight
          // so users can tell this is a "rough" straight line, not a road
          // route.
        }}
      />
    );
  }
  return (
    <Polyline
      path={path}
      options={{
        strokeColor: '#002FA7',
        strokeWeight: 5,
        strokeOpacity: 0.85,
        geodesic: false,
        ...(options || {}),
      }}
    />
  );
}
